/**
 * P4-R2C-PROD-FIX-001 (#794) -- may a background refetch move the local save
 * base version?
 *
 * THE DEFECT. `FunnelDetailsPage` adopted `funnel.draft_version` on every
 * refetch and cleared any displayed conflict, without reconciling
 * `localDraft`. So: a user holds unsaved edits at v8, another writer commits
 * v9, a refetch lands, `baseVersion` silently becomes 9 -- and the user's next
 * Save sends `expected_draft_version: 9` carrying STALE content. The
 * compare-and-swap MATCHES and the other writer's work is overwritten.
 *
 * Server-side atomicity cannot defend against this. The CAS is asked to
 * protect a version the client was never entitled to claim, so the only place
 * to fix it is here: the version and the content it describes must move
 * together, or not at all.
 *
 * Extracted as a function rather than inlined so the rule is executable and
 * the tests can assert what it DOES -- the precedent R2C-003 set after two
 * shape-preserving mutants defeated a purely structural suite.
 */

/** Safe to take the server's version: nothing local would be invalidated. */
export const REBASE = "rebase";
/** Unsaved edits exist and the server moved: report it, change no version. */
export const DIVERGED = "diverged";
/** Nothing to do. */
export const HOLD = "hold";

/**
 * Decide what a refetch may do to the local base version.
 *
 * - no usable server version -> HOLD. Never guess.
 * - no unsaved edits -> REBASE. This is the ordinary initial-load and
 *   clean-refetch path, unchanged from before.
 * - unsaved edits and the server has MOVED -> DIVERGED. The local draft was
 *   built on `baseVersion`; adopting a version whose content we never merged
 *   is what lets a stale Save overwrite another writer.
 * - unsaved edits and the server has NOT moved -> HOLD. Nothing diverged, so
 *   no conflict is reported and nothing is cleared.
 */
export function rebaseDecision({ serverVersion, baseVersion, hasUnsavedChanges }) {
  if (!Number.isInteger(serverVersion)) {
    return { action: HOLD, version: null };
  }
  if (!hasUnsavedChanges) {
    return { action: REBASE, version: serverVersion };
  }
  if (serverVersion !== baseVersion) {
    return { action: DIVERGED, version: serverVersion };
  }
  return { action: HOLD, version: serverVersion };
}

/** Operator-facing text for a divergence found by a refetch. */
export function rebaseDivergenceMessage(baseVersion, serverVersion) {
  return `Another session has modified this funnel (v${baseVersion} → v${serverVersion}). `
    + `Your changes are still here; refresh to get the latest version before saving.`;
}
